/**
 * =============================================================================
 * Página: ReferralCampaigns
 * Versão: 1.1.0 (drill-down por programa)
 *
 * Descrição:
 *   Listagem e gestão das campanhas de indicação do tenant ativo.
 *   - Filtro opcional por programa.
 *   - CRUD via modal `ReferralCampaignFormModal`.
 *   - Mostra janela temporal, recompensa e status.
 *
 *   v1.1.0: Adicionado modo "embedded" para reaproveitamento dentro do
 *   detalhe do programa (`ReferralProgramDetail`). Quando `embedded`:
 *     - Esconde o cabeçalho próprio (o detalhe já tem header contextual).
 *     - Trava o filtro por programa (ignora o seletor).
 *     - Oculta a coluna "Programa" (redundante).
 *     - Pré-seleciona o programa no modal de criação.
 * =============================================================================
 */
import { useState } from 'react';
import { Megaphone, Plus, Pencil, Trash2, Play, MousePointerClick, Users, Target, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import { ReferralQuotaInline } from '@/components/referral/ReferralQuotaInline';
import { ReferralUsageInline } from '@/components/referral/billing/ReferralUsageInline';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollIndicators } from '@/components/ui/scroll-indicators';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  useReferralCampaigns,
  useReferralCampaignMutations,
  type AffiliateCampaignWithMetrics,
} from '@/hooks/useReferralCampaigns';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { ReferralCampaignFormModal } from '@/components/referral/ReferralCampaignFormModal';

const REWARD_LABEL: Record<string, string> = {
  percentage: 'Comissão %',
  fixed: 'Valor fixo',
  points: 'Pontos',
  recurring: 'Recorrente',
  tiered: 'Por tiers',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
};

// Formatadores numéricos pt-BR (instanciados uma única vez por módulo).
const intFormatter = new Intl.NumberFormat('pt-BR');
const buildCurrencyFormatter = (currency: string) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currency || 'BRL',
    maximumFractionDigits: 2,
  });

/**
 * Thumbnail (64x48) da campanha — imagem ou frame de vídeo com badge play.
 * Fallback: ícone neutro com fundo do design system.
 */
function CampaignThumb({
  url,
  type,
  alt,
}: {
  url: string | null;
  type: string | null;
  alt: string;
}) {
  if (!url) {
    return (
      <div className="h-12 w-16 rounded-md bg-muted flex items-center justify-center shrink-0">
        <Megaphone className="h-4 w-4 text-muted-foreground/60" />
      </div>
    );
  }
  if (type === 'video') {
    return (
      <div className="relative h-12 w-16 rounded-md overflow-hidden bg-black shrink-0">
        {/* Preview do primeiro frame; muted/playsInline/preload=metadata para leveza */}
        <video
          src={url}
          className="h-full w-full object-cover"
          muted
          playsInline
          preload="metadata"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Play className="h-4 w-4 text-white fill-white" />
        </div>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      className="h-12 w-16 rounded-md object-cover bg-muted shrink-0"
    />
  );
}

// =============================================================================
// Props (v1.1.0): suportar modo "embedded" dentro do detalhe do programa
// =============================================================================
interface ReferralCampaignsProps {
  /** Quando setado, trava o filtro de programa e oculta o seletor. */
  embeddedProgramId?: string;
  /** Quando true, oculta o cabeçalho da página (drill-down). */
  embedded?: boolean;
}

export default function ReferralCampaigns({
  embeddedProgramId,
  embedded = false,
}: ReferralCampaignsProps = {}) {
  const { toast } = useToast();
  // Quando embedded, o filtro fica fixo no programa do contexto.
  const [programFilter, setProgramFilter] = useState<string>(
    embeddedProgramId ?? 'all',
  );
  const { data: programs = [] } = useReferralPrograms();

  // Em modo embedded, ignora o estado e usa sempre o programa do contexto.
  const effectiveProgramId = embedded
    ? embeddedProgramId ?? null
    : programFilter === 'all'
      ? null
      : programFilter;

  const { data: campaigns = [], isLoading, isError } = useReferralCampaigns(
    effectiveProgramId,
  );
  const { deleteMutation } = useReferralCampaignMutations();
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<AffiliateCampaignWithMetrics | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AffiliateCampaignWithMetrics | null>(null);

  const handleNew = () => {
    setSelected(null);
    setModalOpen(true);
  };

  // Abre modal de nova campanha via ?new=1 (Quick Create do header).
  useAutoOpenFromQuery('1', handleNew);

  const handleEdit = (c: AffiliateCampaignWithMetrics) => {
    setSelected(c);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteMutation.mutateAsync(confirmDelete.id);
      toast({ title: 'Campanha excluída com sucesso.' });
      setConfirmDelete(null);
    } catch (err: any) {
      toast({
        title: 'Erro ao excluir',
        description: err?.message ?? 'Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const noPrograms = programs.length === 0;

  return (
    <div className="space-y-4">
      {/* Header — oculto quando embedded (o detalhe já fornece header) */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <Megaphone className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold truncate">Campanhas</h1>
              <p className="text-xs text-muted-foreground truncate">
                Configure regras de recompensa por campanha dentro de cada programa.
              </p>
            </div>
          </div>
          {/* Padrão TaskList: indicador acima, filtros + ações abaixo */}
          <div className="flex flex-col items-end gap-1.5">
            <ReferralUsageInline className="hidden md:inline-flex" />
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Select value={programFilter} onValueChange={setProgramFilter}>
                <SelectTrigger className="h-9 w-[200px]">
                  <SelectValue placeholder="Filtrar por programa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os programas</SelectItem>
                  {programs.map((prg) => (
                    <SelectItem key={prg.id} value={prg.id}>
                      {prg.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* v1.x — Padronizado com o botão "Novo Afiliado" (size xs, ícone 3.5). */}
              {/* v1.1 — ReferralQuotaInline: badge de uso + intercepta click ao esgotar */}
              <ReferralQuotaInline resource="campaigns">
                <Button onClick={handleNew} size="xs" disabled={noPrograms}>
                  <Plus className="h-3.5 w-3.5" />
                  Nova campanha
                </Button>
              </ReferralQuotaInline>
            </div>
          </div>
        </div>
      )}

      {/* Em modo embedded, mostra apenas a ação "Nova campanha" alinhada à direita */}
      {embedded && (
        <div className="flex justify-end">
          <Button onClick={handleNew} size="xs">
            <Plus className="h-3.5 w-3.5" />
            Nova campanha
          </Button>
        </div>
      )}

      {/* Aviso de pré-requisito (somente fora de embedded — dentro de um programa, ele já existe) */}
      {!embedded && noPrograms && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-sm text-muted-foreground text-center">
            Cadastre primeiro um <strong>programa</strong> para criar campanhas.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-sm text-destructive">
              Erro ao carregar campanhas.
            </div>
          ) : campaigns.length === 0 ? (
            <div className="p-10 text-center">
              <Megaphone className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">
                Nenhuma campanha cadastrada{' '}
                {embedded || programFilter !== 'all' ? 'neste programa' : 'ainda'}.
              </p>
            </div>
          ) : (
            <ScrollIndicators>
            <Table scrollViewport className="min-w-[1500px]">
              <TableHeader>
                <TableRow>
                  {/* v3.0.0: thumbnail visual da campanha */}
                  <TableHead className="w-[88px]">Capa</TableHead>
                  <TableHead>Nome</TableHead>
                  {/* Coluna "Programa" oculta quando embedded (contexto implícito) */}
                  {!embedded && (
                    <TableHead className="hidden xl:table-cell">Programa</TableHead>
                  )}
                  <TableHead className="hidden xl:table-cell">Recompensa</TableHead>
                  <TableHead className="hidden 2xl:table-cell">Período</TableHead>
                  {/* v3.0.0: KPIs agregados (todos os afiliados) */}
                  <TableHead className="hidden md:table-cell text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <MousePointerClick className="h-3.5 w-3.5" /> Cliques
                    </span>
                  </TableHead>
                  <TableHead className="hidden md:table-cell text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <Users className="h-3.5 w-3.5" /> Indicações
                    </span>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <Target className="h-3.5 w-3.5" /> Conversões
                    </span>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <DollarSign className="h-3.5 w-3.5" /> T. Vendas
                    </span>
                  </TableHead>
                  <TableHead className="hidden lg:table-cell text-right">
                    <span className="inline-flex items-center gap-1 justify-end">
                      <DollarSign className="h-3.5 w-3.5" /> Vlr. Comissão
                    </span>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaigns.map((c) => {
                  const currencyFmt = buildCurrencyFormatter(c.currency);
                  return (
                    <TableRow key={c.id}>
                      <TableCell>
                        <CampaignThumb
                          url={c.cover_media_url}
                          type={c.cover_media_type}
                          alt={c.name}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">/{c.slug}</div>
                      </TableCell>
                      {!embedded && (
                        <TableCell className="hidden xl:table-cell text-sm">
                          {c.program_name ?? '—'}
                        </TableCell>
                      )}
                      <TableCell className="hidden xl:table-cell">
                        {REWARD_LABEL[c.reward_type ?? ''] ?? c.reward_type ?? '—'}
                        {c.reward_value !== null && (
                          <span className="text-muted-foreground"> · {c.reward_value}</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden 2xl:table-cell text-xs text-muted-foreground">
                        {formatDate(c.starts_at)} → {formatDate(c.ends_at)}
                      </TableCell>
                      {/* KPIs — tabular-nums para alinhamento perfeito */}
                      <TableCell className="hidden md:table-cell text-right tabular-nums">
                        {intFormatter.format(c.clicks_count)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-right tabular-nums">
                        {intFormatter.format(c.referrals_count)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-right tabular-nums">
                        {intFormatter.format(c.conversions_count)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-right tabular-nums font-medium">
                        {currencyFmt.format(c.total_sales)}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-right tabular-nums font-medium">
                        {currencyFmt.format(c.earned_amount)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                        >
                          <StatusDot tone={c.is_active ? 'emerald' : 'muted'} />
                          {c.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEdit(c)}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setConfirmDelete(c)}
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </ScrollIndicators>
          )}
        </CardContent>
      </Card>

      <ReferralCampaignFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        campaign={selected}
        // v1.1.0: prioriza o programa do contexto (embedded); fallback ao filtro
        defaultProgramId={
          embeddedProgramId ?? (programFilter !== 'all' ? programFilter : null)
        }
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir campanha?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
